/**
 * Cliente de push del navegador.
 *
 * Modelo: este navegador (un `deviceId` estable) tiene como mucho una
 * suscripción, propiedad de la sesión activa. El registro hace upsert por
 * deviceId en el backend, re-vinculando el dispositivo a la cuenta logueada
 * (rebind-on-login). El opt-out vive en servidor (`preferences.push`); aquí no
 * guardamos flags de opt-out.
 */

import { ApiClient } from '@/services/api/client';
import { getDeviceId } from './deviceId';
import { ensureServiceWorker } from './registerServiceWorker';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

const isSupported =
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** Registra la suscripción del navegador en el backend bajo el usuario actual. */
async function registerSubscription(sub: PushSubscription): Promise<void> {
  const json = sub.toJSON();
  if (!json.keys?.p256dh || !json.keys?.auth) return;

  await ApiClient.post('/push/subscribe', {
    deviceId: getDeviceId(),
    endpoint: sub.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
    userAgent: navigator.userAgent,
  });
}

/**
 * Lock módulo-singleton: las llamadas concurrentes comparten el mismo Promise
 * para no lanzar `subscribe()` / registros en paralelo desde distintos
 * consumidores (AuthContext, StudentAreaWrapper, el hook, etc.).
 */
let inFlight: Promise<void> | null = null;

/**
 * Re-vincula la suscripción EXISTENTE de este navegador al usuario actual.
 *
 * - Si el navegador ya tiene una suscripción local, la re-registra (esto
 *   transfiere la propiedad del dispositivo a la sesión activa y cubre la
 *   rotación de endpoint).
 * - Si NO hay suscripción local, no hace nada: respetar que el usuario tenga
 *   las push desactivadas. La recuperación se hace explícitamente desde Ajustes
 *   (`requestAndSubscribe`) o vía `pushsubscriptionchange` en el SW.
 *
 * Nunca pide permisos ni crea una suscripción nueva. Idempotente bajo
 * concurrencia.
 */
export function ensurePushForCurrentUser(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = run().finally(() => {
    inFlight = null;
  });
  return inFlight;

  async function run(): Promise<void> {
    if (!isSupported || Notification.permission !== 'granted') return;
    try {
      const reg = await ensureServiceWorker();
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      await registerSubscription(sub);
    } catch (err) {
      console.error('ensurePushForCurrentUser error:', err);
    }
  }
}

/**
 * Activación explícita desde Ajustes / drawer: pide permiso, crea la
 * suscripción si hace falta y la registra. Devuelve true si quedó suscrito.
 */
export async function requestAndSubscribe(): Promise<boolean> {
  if (!isSupported || !VAPID_PUBLIC_KEY) return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const reg = await ensureServiceWorker();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
    });
  }

  await registerSubscription(sub);
  return true;
}

/**
 * Desactivación explícita desde Ajustes: borra el dispositivo en el backend y
 * cancela la suscripción del navegador.
 */
export async function disablePush(): Promise<void> {
  if (!isSupported) return;

  const reg = await ensureServiceWorker();
  const sub = await reg.pushManager.getSubscription();

  await ApiClient.delete(`/push/subscribe?deviceId=${encodeURIComponent(getDeviceId())}`);

  if (sub) await sub.unsubscribe();
}

/** ¿Tiene este navegador una suscripción push activa? */
export async function hasActiveSubscription(): Promise<boolean> {
  if (!isSupported) return false;
  try {
    const reg = await ensureServiceWorker();
    return (await reg.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}

export const pushSupported = isSupported;
